import React, { useState, useEffect } from 'react';
import { gmDisplayName } from '../utils/displayName';

const TradeNews = () => {
  const [trades, setTrades] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState(''); // '' = all, 'accepted', 'rejected', 'pending'

  useEffect(() => {
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (selectedSeasonId !== '') {
      fetchTrades();
    }
  }, [selectedSeasonId, filterStatus]);

  const fetchSeasons = async () => {
    try {
      const response = await fetch('/api/seasons');
      const data = await response.json();
      
      if (response.ok) {
        setSeasons(data);
        // Set active season as default
        const activeSeason = data.find(s => s.isActive);
        if (activeSeason) {
          setSelectedSeasonId(activeSeason.id);
        } else if (data.length > 0) {
          setSelectedSeasonId(data[0].id);
        }
      } else {
        setError('Failed to load seasons');
      }
    } catch (err) {
      setError('Failed to load seasons');
      console.error(err);
    }
  };

  const fetchTrades = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (selectedSeasonId !== '') params.append('seasonId', selectedSeasonId);
      const response = await fetch(`/api/trades?${params.toString()}`);
      const data = await response.json();
      
      if (response.ok) {
        setTrades(data);
      } else {
        setError('Failed to load trades');
      }
    } catch (err) {
      setError('Failed to load trades');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800', icon: '⏱️', label: 'Pending' },
      accepted: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800', icon: '✅', label: 'Accepted' },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800', icon: '❌', label: 'Rejected' },
      cancelled: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200', icon: '🚫', label: 'Cancelled' },
      withdrawn: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800', icon: '↩️', label: 'Withdrawn' }
    };
    
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlayerFullName = (player) => {
    if (player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`;
    }
    return player.name || 'Unknown Player';
  };

  const acceptedTrades = trades.filter(t => t.status === 'accepted');
  const pendingTrades = trades.filter(t => t.status === 'pending');
  const rejectedTrades = trades.filter(t => t.status === 'rejected');

  if (loading && trades.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading trade news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">🔄 Trade News</h1>
        <p className="text-gray-600 dark:text-gray-400">All trade activity across the league</p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Season Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Season
            </label>
            <select
              value={selectedSeasonId}
              onChange={(e) => setSelectedSeasonId(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.year} {season.isActive ? '(Active)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">All Trades</option>
              <option value="accepted">Accepted Only</option>
              <option value="pending">Pending Only</option>
              <option value="rejected">Rejected Only</option>
              <option value="cancelled">Cancelled Only</option>
              <option value="withdrawn">Withdrawn Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{acceptedTrades.length}</div>
          <div className="text-sm text-green-700 dark:text-green-400">Completed Trades</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 p-4 rounded-lg">
          <div className="text-3xl font-bold text-yellow-600">{pendingTrades.length}</div>
          <div className="text-sm text-yellow-700 dark:text-yellow-400">Pending Trades</div>
        </div>
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">{rejectedTrades.length}</div>
          <div className="text-sm text-red-700 dark:text-red-400">Rejected Trades</div>
        </div>
      </div>

      {/* Trades List */}
      <div className="space-y-4">
        {trades.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-2">📭</div>
            <p>No trades found for the selected filters</p>
          </div>
        ) : (
          trades.map(trade => (
            <div key={trade.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-lg dark:shadow-gray-900 transition">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold mb-1">
                      {trade.thirdTeamId
                        ? [trade.offeringTeam?.name, trade.receivingTeam?.name, trade.thirdTeam?.name]
                            .filter(Boolean).join(' ↔️ ')
                        : `${trade.offeringTeam?.name} ↔️ ${trade.receivingTeam?.name}`}
                      {trade.thirdTeamId && (
                        <span className="ml-2 align-middle bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">
                          🔀 3-Team
                        </span>
                      )}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {formatDate(trade.createdAt)}
                    </p>
                  </div>
                  {getStatusBadge(trade.status)}
                </div>
              </div>

              {/* Trade Details */}
              <div className="p-6">
                {trade.thirdTeamId ? (
                  /* Bei drei Teams hat jeder Spieler sein eigenes Ziel — die
                     Zwei-Spalten-Sicht "A schickt / B schickt" waere falsch.
                     Die Bilanz je Team kommt fertig vom Server (trade.legs). */
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {(trade.legs || []).map(leg => (
                      <div key={leg.teamId}>
                        <h4 className="font-bold text-lg mb-3">{leg.name}</h4>
                        <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase mb-1">gives away</p>
                        <div className="space-y-2 mb-4">
                          {leg.giving.map(player => (
                            <div key={player.id} className="p-3 bg-red-50 border border-red-200 rounded">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-semibold">{getPlayerFullName(player)}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {player.position} | {player.archetypeName || 'N/A'}
                                  </div>
                                </div>
                                <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-sm font-bold">
                                  {player.overallRating} OVR
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase mb-1">receives</p>
                        <div className="space-y-2">
                          {leg.receiving.map(player => (
                            <div key={player.id} className="p-3 bg-green-50 border border-green-200 rounded">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-semibold">{getPlayerFullName(player)}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {player.position} | {player.archetypeName || 'N/A'}
                                  </div>
                                </div>
                                <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-sm font-bold">
                                  {player.overallRating} OVR
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                          {leg.isEqual
                            ? `Rule 5.2 · ${leg.giveTotal} → ${leg.receiveTotal} OVR · diff ${leg.difference}`
                            : `Rule 5.2.1 · ${leg.giveAvg} → ${leg.receiveAvg} avg OVR · diff ${leg.difference}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Offered Players */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-bold text-lg">
                        {trade.offeringTeam?.name}
                      </h4>
                      <span className="text-gray-500 dark:text-gray-400">sends</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Avg OVR: <span className="font-semibold">{trade.offeredPlayersAvgOVR?.toFixed(1)}</span>
                    </p>
                    <div className="space-y-2">
                      {trade.offeredPlayers?.map(player => (
                        <div key={player.id} className="p-3 bg-red-50 border border-red-200 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold">{getPlayerFullName(player)}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                {player.position} | {player.archetypeName || 'N/A'}
                              </div>
                            </div>
                            <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-sm font-bold">
                              {player.overallRating} OVR
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requested Players */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-bold text-lg">
                        {trade.receivingTeam?.name}
                      </h4>
                      <span className="text-gray-500 dark:text-gray-400">sends</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Avg OVR: <span className="font-semibold">{trade.requestedPlayersAvgOVR?.toFixed(1)}</span>
                    </p>
                    <div className="space-y-2">
                      {trade.requestedPlayers?.map(player => (
                        <div key={player.id} className="p-3 bg-green-50 border border-green-200 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold">{getPlayerFullName(player)}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                {player.position} | {player.archetypeName || 'N/A'}
                              </div>
                            </div>
                            <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-sm font-bold">
                              {player.overallRating} OVR
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                )}

                {/* Trade Message */}
                {trade.message && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">💬 Message:</span> {trade.message}
                    </p>
                  </div>
                )}

                {/* Trade Footer */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
                  <div>
                    {trade.processedAt && (
                      <span>
                        Processed: {formatDate(trade.processedAt)}
                      </span>
                    )}
                  </div>
                  <div>
                    {gmDisplayName(trade.processor) && (
                      <span>
                        By: <span className="font-semibold">{gmDisplayName(trade.processor)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TradeNews;
